/**
 * Resourcing Page
 * 资源配置主页面（无顶部 Header，Tab 即为导航）
 */

import React from 'react';
import { Box, CircularProgress, SvgIcon } from '@material-ui/core';
import { Page, TabbedLayout, WarningPanel } from '@backstage/core-components';
import { ClusterManagementTab } from './ClusterManagement';
import { GitIntegrationTab } from './GitIntegration';
import { KeycloakManagementTab } from './KeycloakManagement';
import { ArgoCDManagementTab } from './ArgoCDManagement';
import { TektonManagementTab } from './TektonManagement';
import { useResourcingPermission } from './useResourcingPermission';

// Kubernetes 官方 logo icon
const K8sIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 32 32">
    <path d="M15.9.476a2.14 2.14 0 00-.823.218L3.932 6.01c-.582.277-1.005.804-1.167 1.453L.054 19.874c-.162.65.011 1.336.475 1.895l7.97 9.635c.388.47.96.758 1.575.794h.062c.025.002.049.002.073.002l12.702-.048c.657-.003 1.268-.33 1.678-.9l7.921-9.675c.46-.563.625-1.25.455-1.9L30.236 7.42c-.17-.645-.6-1.167-1.187-1.436L17.755.677a2.232 2.232 0 00-.93-.2H16zm.07 2.14h.013l10.597 4.724c.096.044.146.112.157.185l2.514 11.705c.012.072-.02.156-.09.24l-7.607 9.292c-.07.087-.16.114-.24.114l-12.2.046c-.08 0-.17-.027-.243-.116l-7.65-9.25c-.073-.088-.103-.17-.088-.244l2.675-11.66c.014-.072.066-.14.164-.187L15.176 2.78c.097-.044.17-.06.27-.06l.624-.105zm-.09 4.622c-.258.008-.453.102-.536.295L14 11.03c-.04.09-.1.15-.18.18l-3.7.79c-.38.08-.49.48-.2.73l2.9 2.3c.07.06.1.14.1.22l-.46 3.64c-.05.39.32.6.66.39l3.38-2.4c.08-.04.16-.04.24-.01l3.46 2.3c.34.2.7-.01.64-.4l-.59-3.62c-.01-.08.02-.16.09-.22l2.83-2.39c.28-.24.16-.65-.21-.72l-3.72-.68c-.08-.02-.15-.07-.18-.16L17.61 7.46c-.1-.2-.33-.32-.59-.32h-.04zm.02 1.87l1.08 2.7c.18.44.53.74 1 .83l2.64.5-2.02 1.71c-.37.31-.53.78-.46 1.26l.42 2.58-2.47-1.64c-.2-.13-.42-.2-.64-.2-.19 0-.38.05-.55.14l-2.41 1.71.33-2.6c.06-.48-.11-.94-.49-1.24l-2.07-1.65 2.64-.56c.46-.1.83-.4 1-.85l1-2.64z" fill="#326CE5"/>
  </SvgIcon>
);

// GitLab logo icon
const GitLabIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 32 32">
    <path d="M16 30.468L21.124 14.8H10.876L16 30.468z" fill="#E24329"/>
    <path d="M16 30.468L10.876 14.8H2.67L16 30.468z" fill="#FC6D26"/>
    <path d="M2.67 14.8L.844 20.42a1.25 1.25 0 00.454 1.398L16 30.468 2.67 14.8z" fill="#FCA326"/>
    <path d="M2.67 14.8h8.206L7.592 4.276c-.2-.614-1.064-.614-1.264 0L2.67 14.8z" fill="#E24329"/>
    <path d="M16 30.468L21.124 14.8h8.207L16 30.468z" fill="#FC6D26"/>
    <path d="M29.33 14.8l1.826 5.62a1.25 1.25 0 01-.454 1.398L16 30.468 29.33 14.8z" fill="#FCA326"/>
    <path d="M29.33 14.8h-8.206l3.284-10.524c.2-.614 1.064-.614 1.264 0L29.33 14.8z" fill="#E24329"/>
  </SvgIcon>
);

// Keycloak icon (shield/lock)
const KeycloakIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 2.18l7 3.12v5.7c0 4.83-3.23 9.36-7 10.57V3.18z" fill="#4D4D4D"/>
    <path d="M12 3.18v16.39c3.77-1.21 7-5.74 7-10.57V6.3l-7-3.12z" fill="#008AAA"/>
  </SvgIcon>
);

// ArgoCD icon (circular arrows)
const ArgoCDIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#EF7B4D"/>
    <path d="M12 6v2c2.21 0 4 1.79 4 4h-2l3 3 3-3h-2c0-3.31-2.69-6-6-6z" fill="#EF7B4D"/>
    <path d="M8 12c0-2.21 1.79-4 4-4V6C8.69 6 6 8.69 6 12h2l-3 3-3-3h2z" fill="#EF7B4D"/>
  </SvgIcon>
);

// Tekton icon (pipeline)
const TektonIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M4 6h2v12H4V6zm4 0h2v12H8V6zm6 0l8 6-8 6V6z" fill="#FD495C"/>
  </SvgIcon>
);

export const ResourcingPage: React.FC = () => {
  const { hasPermission, loading } = useResourcingPermission();

  if (loading) {
    return (
      <Page themeId="tool">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (!hasPermission) {
    return (
      <Page themeId="tool">
        <Box p={3}>
          <WarningPanel
            title="访问受限"
            message="您没有权限访问此页面。此页面仅对 system-manager 组成员开放。如需访问，请联系管理员。"
          />
        </Box>
      </Page>
    );
  }

  const tabStyle = { minWidth: 150, minHeight: 52, textTransform: 'none' as const };

  return (
    <Page themeId="tool">
      <TabbedLayout>
        <TabbedLayout.Route
          path="/"
          title="集群管理"
          tabProps={{
            label: (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <K8sIcon style={{ fontSize: 22 }} />
                <span style={{ fontSize: '1.05rem' }}>集群管理</span>
              </Box>
            ),
            style: tabStyle,
          }}
        >
          <ClusterManagementTab />
        </TabbedLayout.Route>
        <TabbedLayout.Route
          path="/git"
          title="Git 仓库"
          tabProps={{
            label: (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <GitLabIcon style={{ fontSize: 22 }} />
                <span style={{ fontSize: '1.05rem' }}>Git 仓库</span>
              </Box>
            ),
            style: tabStyle,
          }}
        >
          <GitIntegrationTab />
        </TabbedLayout.Route>
        <TabbedLayout.Route
          path="/keycloak"
          title="Keycloak"
          tabProps={{
            label: (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <KeycloakIcon style={{ fontSize: 22 }} />
                <span style={{ fontSize: '1.05rem' }}>Keycloak</span>
              </Box>
            ),
            style: tabStyle,
          }}
        >
          <KeycloakManagementTab />
        </TabbedLayout.Route>
        <TabbedLayout.Route
          path="/argocd"
          title="ArgoCD"
          tabProps={{
            label: (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <ArgoCDIcon style={{ fontSize: 22 }} />
                <span style={{ fontSize: '1.05rem' }}>ArgoCD</span>
              </Box>
            ),
            style: tabStyle,
          }}
        >
          <ArgoCDManagementTab />
        </TabbedLayout.Route>
        <TabbedLayout.Route
          path="/tekton"
          title="Tekton"
          tabProps={{
            label: (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <TektonIcon style={{ fontSize: 22 }} />
                <span style={{ fontSize: '1.05rem' }}>Tekton</span>
              </Box>
            ),
            style: tabStyle,
          }}
        >
          <TektonManagementTab />
        </TabbedLayout.Route>
      </TabbedLayout>
    </Page>
  );
};

export default ResourcingPage;
